import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Avatar,
  Box,
  CircularProgress,
  Tooltip,
  ToggleButton,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Chip,
  Pagination,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Info,
  ViewList,
  ViewModule,
  EmojiEvents,
  Star,
  Whatshot,
  LocalPolice,
  MilitaryTech,
  WorkspacePremium,
  Looks,
  Code,
  Html,
  Css,
  Book,
  AccessTime,
  Forum,
  Message,
  Group,
  Person,
} from '@mui/icons-material';
import Button from '../components/UIKit/Button';
import Card from '../components/UIKit/Card';
import Select from '../components/UIKit/Select';
import TextField from '../components/UIKit/TextField';
import ToggleButtonGroup from '../components/UIKit/ToggleButtonGroup';
import { achievementsAPI, coursesAPI, testsAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const AchievementManagement = () => {
  const { t } = useTranslation(['admin', 'common']);
  const [achievements, setAchievements] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'participation',
    icon: 'trophy',
    experiencePoints: 100,
    rarity: 'common',
    category: 'academic',
    requirements: {},
  });
  const [formErrors, setFormErrors] = useState({});
  const [viewMode, setViewMode] = useState(() => {
    // Default to card view on mobile, table on desktop
    return window.innerWidth < 768 ? 'card' : 'table';
  });
  const [courses, setCourses] = useState([]);
  const [tests, setTests] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [testsLoading, setTestsLoading] = useState(false);
  // Параметры пагинации
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });

  // Добавляем ref для отслеживания загрузки данных
  const dataLoadedRef = useRef({
    courses: false,
    tests: false,
  });

  // Типы достижений
  const achievementTypes = [
    {
      value: 'course_completion',
      label: t('admin:achievements.types.course_completion'),
    },
    {
      value: 'test_performance',
      label: t('admin:achievements.types.test_performance'),
    },
    {
      value: 'participation',
      label: t('admin:achievements.types.participation'),
    },
    { value: 'social', label: t('admin:achievements.types.social') },
    { value: 'streak', label: t('admin:achievements.types.streak') },
  ];

  // Редкость достижений
  const rarityTypes = [
    { value: 'common', label: t('admin:achievements.rarities.common') },
    { value: 'rare', label: t('admin:achievements.rarities.rare') },
    { value: 'epic', label: t('admin:achievements.rarities.epic') },
    { value: 'legendary', label: t('admin:achievements.rarities.legendary') },
  ];

  // Категории достижений
  const categoryTypes = [
    { value: 'academic', label: t('admin:achievements.categories.academic') },
    { value: 'social', label: t('admin:achievements.categories.social') },
    { value: 'special', label: t('admin:achievements.categories.special') },
  ];

  // Иконки достижений
  const icons = [
    'trophy',
    'star',
    'medal',
    'badge',
    'award',
    'certificate',
    'crown',
    'javascript',
    'react',
    'html',
    'css',
    'code',
    'book',
    'time',
    'forum',
    'message',
    'group',
    'person',
  ];

  // Add resize listener to update view mode
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768 && viewMode === 'table') {
        setViewMode('card');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [viewMode]);

  const fetchAchievements = useCallback(async () => {
    try {
      setLoading(true);
      const response = await achievementsAPI.getAll({
        page: pagination.page,
        limit: pagination.limit,
      });
      setAchievements(response.data.achievements);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error loading achievements:', error);
      toast.error(t('admin:achievements.messages.error'));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, t]);

  useEffect(() => {
    fetchAchievements();
  }, [fetchAchievements]);

  // Обновляем useEffect для загрузки курсов и тестов чтобы предотвратить рекурсию
  useEffect(() => {
    if (openDialog) {
      const fetchCoursesIfNeeded = async () => {
        // Загружаем курсы только если они еще не были загружены
        if (!dataLoadedRef.current.courses && courses.length === 0) {
          try {
            setCoursesLoading(true);
            // Загружаем все курсы без пагинации
            const response = await coursesAPI.getAll({ limit: 100 });
            setCourses(response.data.courses || []);
            // Помечаем, что курсы загружены
            dataLoadedRef.current.courses = true;
          } catch (error) {
            console.error('Error loading courses:', error);
            toast.error(t('admin:achievements.messages.courses_error'));
          } finally {
            setCoursesLoading(false);
          }
        }
      };

      const fetchTestsIfNeeded = async () => {
        // Загружаем тесты только если они еще не были загружены
        if (!dataLoadedRef.current.tests && tests.length === 0) {
          try {
            setTestsLoading(true);
            // Используем API метод для получения всех тестов
            try {
              // Сначала пробуем получить все тесты через API метод
              const allTestsResponse = await testsAPI.getAll();
              if (
                allTestsResponse.data &&
                Array.isArray(allTestsResponse.data)
              ) {
                const allTests = allTestsResponse.data.map((test) => ({
                  id: test._id,
                  title: test.title || 'Тест без названия',
                }));
                setTests(allTests);
                // Помечаем, что тесты загружены
                dataLoadedRef.current.tests = true;
                setTestsLoading(false);
                return;
              }
            } catch (e) {
              console.log(
                'Не удалось получить все тесты через API, используем запасной вариант:',
                e
              );
            }

            // Если API метод не сработал, используем результаты пользователя
            const response = await testsAPI.getUserResults();

            // Извлекаем уникальные тесты из результатов
            const testsFromResults = Array.from(
              new Map(
                response.data.results
                  .filter((result) => result.test || result.testId) // Фильтруем только результаты с информацией о тесте
                  .map((result) => [
                    result.test?._id || result.testId,
                    {
                      id: result.test?._id || result.testId,
                      title: result.testName || 'Неизвестный тест',
                    },
                  ])
              ).values()
            );

            setTests(testsFromResults);
            // Помечаем, что тесты загружены
            dataLoadedRef.current.tests = true;
          } catch (error) {
            console.error('Error loading tests:', error);
            toast.error(t('admin:achievements.messages.tests_error'));
          } finally {
            setTestsLoading(false);
          }
        }
      };

      fetchCoursesIfNeeded();
      fetchTestsIfNeeded();
    } else {
      // Когда диалог закрывается, сбрасываем флаги загрузки данных
      dataLoadedRef.current = { courses: false, tests: false };
    }
  }, [courses.length, openDialog, t, tests.length]);

  const handleOpenCreateDialog = () => {
    setDialogMode('create');
    setSelectedAchievement(null);
    setFormData({
      title: '',
      description: '',
      type: 'participation',
      icon: 'trophy',
      experiencePoints: 100,
      rarity: 'common',
      category: 'academic',
      requirements: {},
    });
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (achievement) => {
    setDialogMode('edit');
    setSelectedAchievement(achievement);

    // Преобразуем requirements из Map в объект, если необходимо
    const requirements =
      achievement.requirements instanceof Map
        ? Object.fromEntries(achievement.requirements)
        : achievement.requirements || {};

    // Преобразуем courseId в courseIds, если это строка (для обратной совместимости)
    if (
      achievement.type === 'course_completion' &&
      requirements.courseId &&
      !requirements.courseIds
    ) {
      requirements.courseIds = Array.isArray(requirements.courseId)
        ? requirements.courseId
        : [requirements.courseId];
      delete requirements.courseId;
    }

    // Преобразуем testId в testIds, если это строка (для обратной совместимости)
    if (
      achievement.type === 'test_performance' &&
      requirements.testId &&
      !requirements.testIds
    ) {
      requirements.testIds = Array.isArray(requirements.testId)
        ? requirements.testId
        : [requirements.testId];
      delete requirements.testId;
    }

    setFormData({
      title: achievement.title || '',
      description: achievement.description || '',
      type: achievement.type || 'participation',
      icon: achievement.icon || 'trophy',
      experiencePoints: achievement.experiencePoints || 100,
      rarity: achievement.rarity || 'common',
      category: achievement.category || 'academic',
      requirements: requirements,
    });
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleOpenDeleteDialog = (achievement) => {
    setSelectedAchievement(achievement);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedAchievement(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: name === 'experiencePoints' ? parseInt(value, 10) : value,
    }));

    // Очищаем ошибки при изменении поля
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleRequirementsChange = (key, value) => {
    setFormData((prevData) => ({
      ...prevData,
      requirements: {
        ...prevData.requirements,
        [key]: value,
      },
    }));
  };

  const getRequirementsFields = () => {
    const { type, requirements } = formData;

    switch (type) {
      case 'course_completion':
        return (
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="course-select-label">
              {t('admin:achievements.form.courses')}
            </InputLabel>
            <Select
              labelId="course-select-label"
              label={t('admin:achievements.form.courses')}
              value={requirements.courseIds || []}
              onChange={(e) =>
                handleRequirementsChange('courseIds', e.target.value)
              }
              disabled={coursesLoading}
              multiple
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((courseId) => {
                    const course = courses.find((c) => c._id === courseId);
                    return course ? (
                      <Chip key={courseId} label={course.title} />
                    ) : (
                      <Chip key={courseId} label={courseId} />
                    );
                  })}
                </Box>
              )}
            >
              {coursesLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {t('common:loading')}
                </MenuItem>
              ) : (
                courses.map((course) => (
                  <MenuItem key={course._id} value={course._id}>
                    {course.title}
                  </MenuItem>
                ))
              )}
            </Select>
            <FormHelperText>
              {t('admin:achievements.form.courses_helper')}
            </FormHelperText>
          </FormControl>
        );

      case 'test_performance':
        return (
          <>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="test-select-label">
                {t('admin:achievements.form.tests')}
              </InputLabel>
              <Select
                labelId="test-select-label"
                label={t('admin:achievements.form.tests')}
                value={requirements.testIds || []}
                onChange={(e) =>
                  handleRequirementsChange('testIds', e.target.value)
                }
                disabled={testsLoading}
                multiple
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((testId) => {
                      const test = tests.find((t) => t.id === testId);
                      return test ? (
                        <Chip key={testId} label={test.title} />
                      ) : (
                        <Chip key={testId} label={testId} />
                      );
                    })}
                  </Box>
                )}
              >
                {testsLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    {t('common:loading')}
                  </MenuItem>
                ) : (
                  tests.map((test) => (
                    <MenuItem key={test.id} value={test.id}>
                      {test.title}
                    </MenuItem>
                  ))
                )}
              </Select>
              <FormHelperText>
                {t('admin:achievements.form.tests_helper')}
              </FormHelperText>
            </FormControl>
            <TextField
              name="minScore"
              label={t('admin:achievements.form.min_score')}
              type="number"
              fullWidth
              value={requirements.minScore || 80}
              onChange={(e) =>
                handleRequirementsChange(
                  'minScore',
                  parseInt(e.target.value, 10)
                )
              }
              helperText={t('admin:achievements.form.min_score_helper')}
              sx={{ mt: 2 }}
              inputProps={{ min: 0, max: 100 }}
            />
          </>
        );

      case 'participation':
        return (
          <TextField
            name="minExperience"
            label={t('admin:achievements.form.min_experience')}
            type="number"
            fullWidth
            value={requirements.minExperience || 50}
            onChange={(e) =>
              handleRequirementsChange(
                'minExperience',
                parseInt(e.target.value, 10)
              )
            }
            helperText={t('admin:achievements.form.min_experience_helper')}
            sx={{ mt: 2 }}
            inputProps={{ min: 0 }}
          />
        );

      case 'social':
        return (
          <TextField
            name="minSocialAchievements"
            label={t('admin:achievements.form.min_social_achievements')}
            type="number"
            fullWidth
            value={requirements.minSocialAchievements || 1}
            onChange={(e) =>
              handleRequirementsChange(
                'minSocialAchievements',
                parseInt(e.target.value, 10)
              )
            }
            helperText={t(
              'admin:achievements.form.min_social_achievements_helper'
            )}
            sx={{ mt: 2 }}
            inputProps={{ min: 1 }}
          />
        );

      case 'streak':
        return (
          <TextField
            name="minStreak"
            label={t('admin:achievements.form.min_streak')}
            type="number"
            fullWidth
            value={requirements.minStreak || 5}
            onChange={(e) =>
              handleRequirementsChange(
                'minStreak',
                parseInt(e.target.value, 10)
              )
            }
            helperText={t('admin:achievements.form.min_streak_helper')}
            sx={{ mt: 2 }}
            inputProps={{ min: 1 }}
          />
        );

      default:
        return null;
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = t('admin:achievements.form.title_required');
    }

    if (!formData.description.trim()) {
      errors.description = t('admin:achievements.form.description_required');
    }

    if (!formData.type) {
      errors.type = t('admin:achievements.form.type_required');
    }

    if (!formData.icon) {
      errors.icon = t('admin:achievements.form.icon_required');
    }

    if (formData.experiencePoints <= 0) {
      errors.experiencePoints = t(
        'admin:achievements.form.experience_required'
      );
    }

    if (!formData.rarity) {
      errors.rarity = t('admin:achievements.form.rarity_required');
    }

    if (!formData.category) {
      errors.category = t('admin:achievements.form.category_required');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveAchievement = async () => {
    if (!validateForm()) return;

    try {
      setDialogLoading(true);

      // Получаем копию данных формы для отправки
      const dataToSend = { ...formData };

      // Дополнительная проверка для требований
      if (Object.keys(formData.requirements).length === 0) {
        // Устанавливаем минимальные требования в зависимости от типа
        switch (formData.type) {
          case 'course_completion':
            if (
              !dataToSend.requirements.courseIds ||
              dataToSend.requirements.courseIds.length === 0
            ) {
              toast.error(t('admin:achievements.form.courses_required'));
              setDialogLoading(false);
              return;
            }
            break;
          case 'test_performance':
            if (
              !dataToSend.requirements.testIds ||
              dataToSend.requirements.testIds.length === 0
            ) {
              toast.error(t('admin:achievements.form.tests_required'));
              setDialogLoading(false);
              return;
            }
            if (!dataToSend.requirements.minScore) {
              dataToSend.requirements.minScore = 80;
            }
            break;
          case 'participation':
            if (!dataToSend.requirements.minExperience) {
              dataToSend.requirements.minExperience = 50;
            }
            break;
          case 'social':
            if (!dataToSend.requirements.minSocialAchievements) {
              dataToSend.requirements.minSocialAchievements = 1;
            }
            break;
          case 'streak':
            if (!dataToSend.requirements.minStreak) {
              dataToSend.requirements.minStreak = 5;
            }
            break;
          default:
            break;
        }
      }

      if (dialogMode === 'create') {
        await achievementsAPI.create(dataToSend);
        toast.success(t('admin:achievements.messages.create_success'));
      } else {
        await achievementsAPI.update(selectedAchievement._id, dataToSend);
        toast.success(t('admin:achievements.messages.update_success'));
      }

      handleCloseDialog();
      fetchAchievements();
    } catch (error) {
      console.error('Error saving achievement:', error);
      toast.error(t('admin:achievements.messages.error'));
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDeleteAchievement = async () => {
    try {
      await achievementsAPI.delete(selectedAchievement._id);
      toast.success(t('admin:achievements.messages.delete_success'));
      handleCloseDeleteDialog();
      fetchAchievements();
    } catch (error) {
      console.error('Error deleting achievement:', error);
      toast.error(t('admin:achievements.messages.error'));
    }
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // Получить цвет для отображения редкости достижения
  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common':
        return '#8bc34a'; // green
      case 'rare':
        return '#2196f3'; // blue
      case 'epic':
        return '#9c27b0'; // purple
      case 'legendary':
        return '#ff9800'; // orange
      default:
        return '#757575'; // grey
    }
  };

  // Получить иконку в зависимости от типа
  const getIconComponent = (iconName) => {
    const iconToRender = iconName || 'trophy';

    switch (iconToRender) {
      case 'trophy':
        return <EmojiEvents />;
      case 'star':
        return <Star />;
      case 'medal':
        return <Whatshot />;
      case 'badge':
        return <LocalPolice />;
      case 'award':
        return <MilitaryTech />;
      case 'certificate':
        return <WorkspacePremium />;
      case 'crown':
        return <Looks />;
      case 'javascript':
        return <Code />;
      case 'react':
        return <Code />;
      case 'presentation':
        return <Html />;
      case 'css':
        return <Css />;
      case 'code':
        return <Code />;
      case 'book':
        return <Book />;
      case 'time':
        return <AccessTime />;
      case 'forum':
        return <Forum />;
      case 'message':
        return <Message />;
      case 'group':
        return <Group />;
      case 'person':
        return <Person />;
      default:
        return <EmojiEvents />;
    }
  };

  // Обработчик изменения страницы пагинации
  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <Grid
        container
        spacing={{ xs: 1, lg: 2 }}
        sx={{
          mb: 3,
          display: 'flex',
          flexWrap: 'wrap',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
        }}
      >
        <Grid item xs={12} lg>
          <Typography variant="h5" component="h1">
            {t('admin:achievements.title')}
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          lg={4}
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            justifyContent: { xs: 'space-between', md: 'flex-end' },
            gap: 2,
            width: '100%',
          }}
        >
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreateDialog}
            sx={{
              flexShrink: 0,
            }}
          >
            {t('admin:achievements.add')}
          </Button>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="view mode"
          >
            <ToggleButton value="table" aria-label="table view">
              <ViewList />
            </ToggleButton>
            <ToggleButton value="card" aria-label="card view">
              <ViewModule />
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : achievements.length === 0 ? (
        <Paper elevation={0} sx={{ p: 3, textAlign: 'center' }}>
          <Info color="info" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h6">
            {t('admin:achievements.list.empty')}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {t('admin:achievements.list.empty_description')}
          </Typography>
        </Paper>
      ) : viewMode === 'table' ? (
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('admin:achievements.list.icon')}</TableCell>
                <TableCell>{t('admin:achievements.list.title')}</TableCell>
                <TableCell>
                  {t('admin:achievements.list.description')}
                </TableCell>
                <TableCell>{t('admin:achievements.list.type')}</TableCell>
                <TableCell>{t('admin:achievements.list.experience')}</TableCell>
                <TableCell>{t('admin:achievements.list.rarity')}</TableCell>
                <TableCell>{t('admin:achievements.list.category')}</TableCell>
                <TableCell align="right">
                  {t('admin:achievements.list.actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {achievements.map((achievement) => (
                <TableRow key={achievement._id}>
                  <TableCell>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: getRarityColor(achievement.rarity),
                      }}
                    >
                      {getIconComponent(achievement.icon)}
                    </Avatar>
                  </TableCell>
                  <TableCell>{achievement.title}</TableCell>
                  <TableCell>{achievement.description}</TableCell>
                  <TableCell>
                    {t(`admin:achievements.types.${achievement.type}`)}
                  </TableCell>
                  <TableCell>{achievement.experiencePoints}</TableCell>
                  <TableCell>
                    <Chip
                      label={t(
                        `admin:achievements.rarities.${achievement.rarity}`
                      )}
                      sx={{
                        bgcolor: getRarityColor(achievement.rarity),
                        color: 'white',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {t(`admin:achievements.categories.${achievement.category}`)}
                  </TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Tooltip title={t('admin:achievements.edit')}>
                        <IconButton
                          onClick={() => handleOpenEditDialog(achievement)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('admin:achievements.delete.title')}>
                        <IconButton
                          onClick={() => handleOpenDeleteDialog(achievement)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Grid container spacing={3}>
          {achievements.map((achievement) => (
            <Grid item xs={12} sm={6} lg={4} xxl={3} key={achievement._id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: getRarityColor(achievement.rarity),
                        mr: 2,
                      }}
                    >
                      {getIconComponent(achievement.icon)}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ fontSize: '1rem', fontWeight: 600 }}
                      >
                        {achievement.title}
                      </Typography>
                      <Chip
                        label={t(
                          `admin:achievements.rarities.${achievement.rarity}`
                        )}
                        size="small"
                        sx={{
                          bgcolor: getRarityColor(achievement.rarity),
                          color: 'white',
                          fontSize: '0.75rem',
                          height: 20,
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {achievement.description}
                  </Typography>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>{t('admin:achievements.list.type')}:</strong>{' '}
                      {t(`admin:achievements.types.${achievement.type}`)}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>
                        {t('admin:achievements.list.experience')}:
                      </strong>{' '}
                      {achievement.experiencePoints} XP
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>{t('admin:achievements.list.category')}:</strong>{' '}
                      {t(
                        `admin:achievements.categories.${achievement.category}`
                      )}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Tooltip title={t('admin:achievements.edit')}>
                    <IconButton
                      onClick={() => handleOpenEditDialog(achievement)}
                      color="primary"
                      size="small"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('admin:achievements.delete.title')}>
                    <IconButton
                      onClick={() => handleOpenDeleteDialog(achievement)}
                      color="error"
                      size="small"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Компонент пагинации */}
      {!loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4,
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              {t('common:pagination.rows_per_page')}
            </Typography>
            <Select
              value={pagination.limit}
              onChange={(e) => setPagination(prev => ({ ...prev, page: 1, limit: Number(e.target.value) }))}
              size="small"
              sx={{ minWidth: 80 }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </Box>
          
          {pagination.pages > 1 && (
            <Pagination 
              count={pagination.pages} 
              page={pagination.page} 
              onChange={handlePageChange} 
              color="primary" 
              showFirstButton 
              showLastButton 
            />
          )}
          
          <Typography variant="body2" color="text.secondary">
            {t('common:pagination.showing_items', { 
              from: Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total),
              to: Math.min(pagination.page * pagination.limit, pagination.total),
              total: pagination.total
            })}
          </Typography>
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6">
            {dialogMode === 'create'
              ? t('admin:achievements.add')
              : t('admin:achievements.edit')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              name="title"
              label={t('admin:achievements.form.title')}
              fullWidth
              value={formData.title}
              onChange={handleChange}
              required
              error={!!formErrors.title}
              helperText={formErrors.title}
            />
            <TextField
              name="description"
              label={t('admin:achievements.form.description')}
              fullWidth
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
              required
              error={!!formErrors.description}
              helperText={formErrors.description}
            />
            <Select
              name="type"
              label={t('admin:achievements.form.type')}
              fullWidth
              value={formData.type}
              onChange={handleChange}
              required
              error={!!formErrors.type}
              helperText={formErrors.type}
            >
              {achievementTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
            <FormControl fullWidth error={!!formErrors.icon}>
              <Select
                labelId="icon-select-label"
                name="icon"
                value={formData.icon}
                onChange={handleChange}
                label={t('admin:achievements.form.icon')}
              >
                {icons.map((icon) => (
                  <MenuItem key={icon} value={icon}>
                    {icon}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.icon && (
                <FormHelperText>{formErrors.icon}</FormHelperText>
              )}
            </FormControl>
            <TextField
              name="experiencePoints"
              label={t('admin:achievements.form.experience')}
              type="number"
              fullWidth
              value={formData.experiencePoints}
              onChange={handleChange}
              required
              error={!!formErrors.experiencePoints}
              helperText={formErrors.experiencePoints}
              inputProps={{ min: 1 }}
            />
            <Select
              name="rarity"
              label={t('admin:achievements.form.rarity')}
              fullWidth
              value={formData.rarity}
              onChange={handleChange}
              required
              error={!!formErrors.rarity}
              helperText={formErrors.rarity}
            >
              {rarityTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
            <Select
              name="category"
              label={t('admin:achievements.form.category')}
              fullWidth
              value={formData.category}
              onChange={handleChange}
              required
              error={!!formErrors.category}
              helperText={formErrors.category}
            >
              {categoryTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>

            <Typography variant="subtitle1" gutterBottom>
              {t('admin:achievements.form.requirements')}
            </Typography>
            {getRequirementsFields()}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="text" onClick={handleCloseDialog}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleSaveAchievement}
            color="primary"
            variant="contained"
            disabled={dialogLoading}
          >
            {dialogLoading ? (
              <CircularProgress size={24} />
            ) : (
              t('common:actions.save')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6">
            {t('admin:achievements.delete.title')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t('admin:achievements.delete.confirm')}
          </Typography>
          {selectedAchievement && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, fontWeight: 'bold' }}
            >
              {selectedAchievement.title}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="text" onClick={handleCloseDeleteDialog}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleDeleteAchievement}
            color="error"
            variant="contained"
          >
            {t('common:actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AchievementManagement;
